import { Hand } from 'lucide-react';
import {
  Button,
  Card,
  Description,
  EmptyState,
  Heading,
  Table,
} from '@heroui/react';
import type { SpotUsage } from '../../../api/my-parking-spots';
import { UsageTypeChip } from '../../../components/ui/usage-chips';
import { formatUsagePeriod } from '../utils/usage-display';

interface BorrowedUsagesSectionProps {
  usages: SpotUsage[];
  onCancel: (usageId: string) => void;
  onFinish: (usageId: string) => void;
}

export function BorrowedUsagesSection({ usages, onCancel, onFinish }: BorrowedUsagesSectionProps) {
  if (usages.length === 0) {
    return (
      <EmptyState className="flex flex-col items-center gap-2 py-12 text-center">
        <Hand size={40} className="text-muted" />
        <Heading level={3}>Nenhuma vaga em uso</Heading>
        <Description>
          Quando você solicitar uma vaga disponível, ela aparecerá aqui.
        </Description>
      </EmptyState>
    );
  }

  return (
    <>
      <div className="hidden md:block overflow-x-auto">
        <Table aria-label="Vagas em uso">
          <Table.ScrollContainer>
            <Table.Content>
              <Table.Header>
                <Table.Column isRowHeader>Vaga</Table.Column>
                <Table.Column>Tipo</Table.Column>
                <Table.Column>Período</Table.Column>
                <Table.Column className="text-right">Ações</Table.Column>
              </Table.Header>
              <Table.Body items={usages}>
                {(usage) => (
                  <Table.Row id={usage.id}>
                    <Table.Cell>
                      <span className="font-mono font-medium">{usage.spotCode}</span>
                    </Table.Cell>
                    <Table.Cell>
                      <UsageTypeChip type={usage.type} />
                    </Table.Cell>
                    <Table.Cell className="text-sm text-muted">
                      {formatUsagePeriod(usage.startAt, usage.endAt)}
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="secondary" onPress={() => onCancel(usage.id)}>
                          Cancelar
                        </Button>
                        <Button size="sm" variant="primary" onPress={() => onFinish(usage.id)}>
                          Liberar vaga
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
      </div>

      <div className="flex flex-col gap-3 md:hidden">
        {usages.map((usage) => (
          <Card key={usage.id}>
            <Card.Header className="flex-row items-start justify-between gap-3 pb-0">
              <div>
                <Card.Title className="font-mono">{usage.spotCode}</Card.Title>
                <Card.Description>
                  {formatUsagePeriod(usage.startAt, usage.endAt)}
                </Card.Description>
              </div>
              <UsageTypeChip type={usage.type} />
            </Card.Header>
            <Card.Footer className="flex gap-2 pt-3">
              <Button variant="secondary" className="flex-1" onPress={() => onCancel(usage.id)}>
                Cancelar
              </Button>
              <Button variant="primary" className="flex-1" onPress={() => onFinish(usage.id)}>
                Liberar vaga
              </Button>
            </Card.Footer>
          </Card>
        ))}
      </div>
    </>
  );
}
